import { useEffect, useRef, useState } from 'react';
import {
  isParentFormHeightRequest,
  notifyParentFormHeight,
  notifyParentFormReady,
} from '../lib/analytics';

/**
 * Formulaire public en V2 (`?mode=iframe&v=2`) : synchronise sa hauteur avec
 * l'iframe parente, après une poignée de main avec le site.
 *
 * 1. Le formulaire annonce `DESERTOURS_FORM_READY` au site (`VITE_PARENT_ORIGIN`).
 * 2. Le site répond `DESERTOURS_FORM_HEIGHT_REQUEST` ; seule une demande de la
 *    fenêtre parente, depuis cette origine, est acceptée.
 * 3. Le hook renvoie alors `true` (rendu V2) et publie la hauteur, puis chaque
 *    changement. Chaque nouvelle demande republie la hauteur, même inchangée :
 *    le site la redemande s'il a raté le premier message.
 *
 * Sans réponse (site sans V2, origine fausse), le hook renvoie `false` et la page
 * garde son rendu historique : le site garde alors son cadre actuel.
 *
 * Hauteur publiée = celle de #root, ou plus si un élément en position absolue
 * déborde dessous (liste d'écoles ouverte sous le dernier champ) : rien ne doit
 * être coupé par le bas du cadre. On ne mesure pas le document : sa hauteur ne
 * descend jamais sous celle du cadre, qui ne pourrait alors plus raccourcir.
 */
export function useParentFrameHeight(requested: boolean): boolean {
  const [confirmed, setConfirmed] = useState(false);
  // Republication forcée, branchée par l'effet d'observation quand il tourne.
  const republishRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!requested) return;

    function handleMessage(event: MessageEvent) {
      if (!isParentFormHeightRequest(event)) return;
      setConfirmed(true);
      republishRef.current?.();
    }

    window.addEventListener('message', handleMessage);
    notifyParentFormReady();
    return () => window.removeEventListener('message', handleMessage);
  }, [requested]);

  const enabled = requested && confirmed;

  useEffect(() => {
    if (!enabled) return;

    const root = document.getElementById('root');
    if (!root) {
      console.warn('[PublicForm] Redimensionnement iframe ignoré : noeud #root absent');
      return;
    }
    const rootElement = root;

    let animationFrameId = 0;
    let lastHeight = 0;
    let isActive = true;

    function publishHeight() {
      animationFrameId = 0;
      const height = Math.ceil(
        Math.max(rootElement.getBoundingClientRect().height, rootElement.scrollHeight)
      );
      if (height <= 0 || height === lastHeight) return;

      lastHeight = height;
      notifyParentFormHeight(height);
    }

    function scheduleHeightUpdate() {
      if (!isActive || animationFrameId !== 0) return;
      animationFrameId = window.requestAnimationFrame(publishHeight);
    }

    republishRef.current = () => {
      lastHeight = 0;
      scheduleHeightUpdate();
    };

    // ResizeObserver : changements de taille de #root (erreurs, partie copilote,
    // polices, largeur). MutationObserver en plus : une liste en position absolue
    // déborde sans changer la taille de #root.
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleHeightUpdate);
    resizeObserver?.observe(rootElement);

    const mutationObserver = new MutationObserver(scheduleHeightUpdate);
    mutationObserver.observe(rootElement, { childList: true, subtree: true });

    window.addEventListener('resize', scheduleHeightUpdate);
    scheduleHeightUpdate();

    void document.fonts?.ready.then(scheduleHeightUpdate);

    return () => {
      isActive = false;
      republishRef.current = null;
      if (animationFrameId !== 0) {
        window.cancelAnimationFrame(animationFrameId);
      }
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', scheduleHeightUpdate);
    };
  }, [enabled]);

  return enabled;
}
